// What's New — small button in the topbar that opens a panel listing
// recent deploys in operator language. Source of truth lives in
// `services/app/frontend/src/whatsnew.ts`; this component just renders.
//
// Unread tracking: localStorage key `mdh_whatsnew_seen` stores the id of
// the latest entry the operator has acknowledged. If the latest entry's
// id is newer (i.e. different + appears earlier in the array, since we
// keep newest-first), a small dot appears.
//
// We don't track per-entry read state — operators read the panel as a
// stack, and "newer than what I saw last time" is the only signal that
// matters. Opening the panel marks everything seen.

import React, { useEffect, useRef, useState } from 'react';
import { WHATS_NEW, type WhatsNewEntry } from '../whatsnew';

const SEEN_KEY = 'mdh_whatsnew_seen';

function readSeen(): string | null {
  try { return localStorage.getItem(SEEN_KEY); } catch { return null; }
}
function writeSeen(id: string): void {
  try { localStorage.setItem(SEEN_KEY, id); } catch { /* private mode */ }
}

// Count of entries newer than the last-seen id. On first-ever visit we return
// 0 (not the full length) — VW-D4 in UPGRADE_UX_TODO.md flagged that a brand-
// new operator seeing "9+" / "18 unread" for content that predates them is a
// trust-killer in the first 5 seconds. The first-mount effect seeds `seen` to
// the current latest id so any genuinely NEW entry published after they joined
// still surfaces a badge.
function unreadCount(seen: string | null): number {
  if (!seen) return 0;
  const idx = WHATS_NEW.findIndex(e => e.id === seen);
  if (idx < 0) return 0; // seen id no longer exists — treat as already-caught-up
  return idx; // entries before the seen index are newer
}

export default function WhatsNewButton(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<string | null>(() => readSeen());
  // Snapshot of unread count taken when the panel opens — held for the
  // lifetime of the open state so badges don't disappear on the same render
  // that marks them seen. Persisted to seen + cleared on close.
  const [openSnapshotUnread, setOpenSnapshotUnread] = useState<number>(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);
  const everOpenedRef = useRef(false);
  const unreadBadge = open ? openSnapshotUnread : unreadCount(seen);

  // First-mount seed (VW-D4): if the operator has never opened the panel, mark
  // the current latest entry as seen so the badge doesn't shout "9+" on a
  // brand-new account. Genuinely new entries published AFTER this mount still
  // bump the badge because the seen id stays pinned to whatever was latest at
  // first visit.
  useEffect(() => {
    if (seen === null && WHATS_NEW.length > 0) {
      const latest = WHATS_NEW[0]!.id;
      writeSeen(latest);
      setSeen(latest);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click-outside dismiss + Escape key.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Snapshot unread on open, persist seen on close. We persist on close (not
  // open) so a quick peek still marks everything read, while the badges stay
  // visible during the operator's read. Initial mount with open=false must
  // NOT persist — that would silently drop the unread badge before the
  // operator ever sees the panel. everOpenedRef guards that transition.
  useEffect(() => {
    if (open) {
      setOpenSnapshotUnread(unreadCount(seen));
      everOpenedRef.current = true;
    } else if (everOpenedRef.current && WHATS_NEW.length > 0) {
      const latest = WHATS_NEW[0]!.id;
      if (latest !== seen) { writeSeen(latest); setSeen(latest); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggleExpanded(id: string): void {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title={unreadBadge > 0 ? `${unreadBadge} new since you last looked` : "What's new in Data Hub"}
        style={{
          background: 'transparent', border: '1px solid var(--rail-line)',
          borderRadius: 4, padding: '4px 10px', fontSize: 11,
          fontFamily: 'var(--mono)', color: 'var(--rail-ink-2)',
          cursor: 'pointer', position: 'relative', display: 'inline-flex',
          alignItems: 'center', gap: 5,
        }}
      >
        what's new
        {unreadBadge > 0 && (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 14, height: 14, padding: '0 4px',
              borderRadius: 7, fontSize: 9, fontWeight: 700,
              background: 'oklch(0.55 0.18 30)', color: 'white',
              fontFamily: 'var(--mono)',
            }}
            aria-label={`${unreadBadge} unread`}
          >{unreadBadge > 9 ? '9+' : unreadBadge}</span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="What's new"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200,
            width: 'min(420px, calc(100vw - 24px))',
            maxHeight: 'min(560px, calc(100vh - 80px))',
            overflowY: 'auto',
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            boxShadow: '0 10px 30px oklch(0.2 0.02 250 / 0.18)',
          }}
        >
          <div style={{
            padding: '12px 14px 10px',
            borderBottom: '1px solid var(--line-2)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>What's new</span>
            <span style={{ fontSize: 10.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
              problem-first · newest at top
            </span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0' }}>
            {WHATS_NEW.map((e: WhatsNewEntry, i: number) => {
              const isUnread = i < openSnapshotUnread;
              const isExpanded = expanded.has(e.id);
              return (
                <li
                  key={e.id}
                  style={{
                    padding: '10px 14px',
                    borderBottom: i < WHATS_NEW.length - 1 ? '1px solid var(--line-2)' : 'none',
                    background: isUnread ? 'color-mix(in oklch, var(--bg-elev), oklch(0.55 0.18 250) 4%)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    {isUnread && (
                      <span
                        aria-label="new"
                        style={{
                          width: 6, height: 6, borderRadius: 3,
                          background: 'oklch(0.55 0.18 30)', flexShrink: 0,
                          marginTop: 5,
                        }}
                      />
                    )}
                    <span style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35, flex: 1 }}>{e.title}</span>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-3)', flexShrink: 0 }}>{e.date}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5, marginLeft: isUnread ? 14 : 0 }}>
                    {e.problem}
                  </div>
                  {e.details && e.details.length > 0 && (
                    <div style={{ marginLeft: isUnread ? 14 : 0, marginTop: 6 }}>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(e.id)}
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          color: 'var(--accent, oklch(0.55 0.18 250))',
                          fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer',
                        }}
                      >
                        {isExpanded ? 'less ↑' : `more · ${e.details.length} detail${e.details.length === 1 ? '' : 's'} ↓`}
                      </button>
                      {isExpanded && (
                        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
                          {e.details.map((d, di) => <li key={di} style={{ marginBottom: 3 }}>{d}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div style={{
            padding: '8px 14px',
            borderTop: '1px solid var(--line-2)',
            fontSize: 10.5, color: 'var(--ink-3)',
          }}>
            Curated from <code style={{ fontFamily: 'var(--mono)' }}>RELEASES.md</code>. Want full technical detail? See the repo.
          </div>
        </div>
      )}
    </div>
  );
}
